import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ThumbsUp, Loader2 } from "lucide-react";

export default function RoadmapPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // item_id being voted on

  const load = async () => {
    try {
      const { data } = await api.get("/roadmap");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  const vote = async (id) => {
    if (!user || !user.email) {
      toast.info("Please log in to vote.");
      return;
    }
    setBusy(id);
    try {
      await api.post(`/roadmap/${id}/vote`);
      await load();
    } catch (e) {
      toast.error("Could not record your vote.");
    } finally { setBusy(null); }
  };

  return (
    <main className="max-w-5xl mx-auto px-4 md:px-8 py-14 md:py-20" data-testid="roadmap-page">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Public Roadmap</span></div>
      <h1 className="font-display text-4xl md:text-5xl mv-green-text mt-3">
        You choose what we build <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>next.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--mv-muted)" }}>
        Vote on which countries, land types and flexible-payment options we should prioritise.
        One vote per member per idea — you can change your vote anytime.
      </p>

      {!user?.email && (
        <div className="mt-6 mv-card p-4 flex flex-wrap gap-3 items-center justify-between" style={{ background: "rgba(212,175,55,0.10)" }}>
          <div className="text-[14px]">Log in to place your vote.</div>
          <Link to="/login" className="btn-green !py-2" data-testid="roadmap-login-cta">Log in to vote</Link>
        </div>
      )}

      <div className="mt-8 space-y-3" data-testid="roadmap-list">
        {loading && <div className="text-neutral-500">Loading roadmap…</div>}
        {items.map((it) => (
          <div key={it.id} className="mv-card p-5 flex gap-4 items-center">
            <button
              onClick={() => vote(it.id)}
              className={`shrink-0 w-16 h-16 rounded-2xl flex flex-col items-center justify-center transition-colors ${
                it.voted_by_me ? "mv-green-bg text-white" : "bg-black/5 mv-green-text hover:bg-black/10"
              }`}
              data-testid={`vote-${it.id}`}
              disabled={busy === it.id}
              aria-label={`Vote for ${it.title}`}
            >
              {busy === it.id ? <Loader2 className="animate-spin" size={20}/> : <>
                <ThumbsUp size={18}/>
                <span className="font-display text-lg mt-0.5">{it.votes}</span>
              </>}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-display text-lg mv-green-text">{it.title}</div>
                <span className="text-[10.5px] tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ background: "rgba(10,59,40,0.08)", color: "var(--mv-green)" }}>
                  {it.category}
                </span>
              </div>
              <p className="mt-1 text-[14px]" style={{ color: "var(--mv-muted)" }}>{it.description}</p>
            </div>
          </div>
        ))}
        {!loading && !items.length && (
          <div className="mv-card p-6 text-neutral-500">No roadmap items yet.</div>
        )}
      </div>
    </main>
  );
}
